import EmptyState from '@/components/shared/EmptyState'
import { formatCurrency, r2 } from '@/lib/utils'
import { IconHeadset } from '@/components/icons/Icons'
import type { ConfigTarifaCliente, Tarea } from '@/lib/types'

interface TabSoporteProps {
  empresaId: number
  tarifaConfig: ConfigTarifaCliente | null
  tareasSoporte: Tarea[]
}

export default function TabSoporte({ tarifaConfig, tareasSoporte }: TabSoporteProps) {
  if (!tarifaConfig) {
    return (
      <EmptyState
        icon={<IconHeadset size={40} />}
        title="Sin tarifa configurada"
        description="Esta empresa aun no tiene una tarifa de soporte configurada. Configurala en la pestana Configuracion."
      />
    )
  }

  const horasUsadas = r2(tareasSoporte.reduce((s, t) => s + (t.horas_reales || 0), 0))
  const umbral = tarifaConfig.umbral_horas_mes
  const porcentajeUso = umbral > 0 ? Math.min(100, r2((horasUsadas / umbral) * 100)) : 0

  return (
    <div>
      <div className="empresa-section-card">
        <h4 className="empresa-section-title">Tarifa de Soporte</h4>
        <div className="empresa-tarifa-grid">
          <div className="empresa-tarifa-item">
            <span className="empresa-tarifa-item__label">Tarifa por hora</span>
            <span className="empresa-tarifa-item__value">
              {formatCurrency(tarifaConfig.tarifa_hora, tarifaConfig.moneda)}
            </span>
          </div>
          <div className="empresa-tarifa-item">
            <span className="empresa-tarifa-item__label">Umbral mensual</span>
            <span className="empresa-tarifa-item__value">{umbral}h/mes</span>
          </div>
          <div className="empresa-tarifa-item">
            <span className="empresa-tarifa-item__label">Moneda</span>
            <span className="empresa-tarifa-item__value">{tarifaConfig.moneda}</span>
          </div>
        </div>

        {umbral > 0 && (
          <div className="empresa-hours-progress">
            <div className="empresa-hours-progress__header">
              <span>{horasUsadas}h usadas de {umbral}h</span>
              <span>{porcentajeUso}%</span>
            </div>
            <div className="empresa-hours-progress__track">
              <div
                className={`empresa-hours-progress__fill ${porcentajeUso >= 90 ? 'empresa-hours-progress__fill--danger' : porcentajeUso >= 70 ? 'empresa-hours-progress__fill--warning' : ''}`}
                style={{ width: `${porcentajeUso}%` }}
              />
            </div>
          </div>
        )}
      </div>

      <div className="empresa-section-card" style={{ marginTop: 16 }}>
        <h4 className="empresa-section-title">Tareas Facturadas (por horas)</h4>
        {tareasSoporte.length === 0 ? (
          <p className="empresa-empty-text">Sin tareas facturadas por horas</p>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Tarea</th>
                  <th>Horas Est.</th>
                  <th>Horas Real</th>
                  <th>Estado</th>
                  <th>Monto</th>
                </tr>
              </thead>
              <tbody>
                {tareasSoporte.map(t => (
                  <tr key={t.id}>
                    <td>{t.titulo}</td>
                    <td>{t.horas_estimadas}h</td>
                    <td>{t.horas_reales}h</td>
                    <td>
                      <span className={`badge badge-tarea-${t.estado}`}>{t.estado}</span>
                    </td>
                    <td style={{ fontWeight: 700 }}>{formatCurrency(t.monto, 'USD')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
